import { makeObservable, observable, action, flow } from "mobx";
import { FoldersData } from "../../../pages/v2/DrivesPage/mockData";
import {
    createFolder as createFolderService,
    tenantRenameFolder,
    duplicateFolder as duplicateFolderService
} from "../../../services/folders";

class FoldersStore {
  folders = [];
  isLoading = false;
  error = null;

  isCreatingFolder = false;
  createdFolder = false;
  folderCreated = null;
  folderAlreadyExists = false;
  isErrorCreateFolder = false;

  isRenamingFolder = false;
  folderRenamed = false;
  renamedFolder = null;
  isErrorRenameFolder = false;
  errorRenameFolderMessage = '';
  successRenameFolderMessage = '';

  constructor() {
    makeObservable(this, {
      // Observables
      folders: observable,
      isLoading: observable,
      error: observable,
      isCreatingFolder: observable,
      createdFolder: observable,
      folderCreated: observable,
      folderAlreadyExists: observable,
      isErrorCreateFolder: observable,
      isRenamingFolder: observable,
      folderRenamed: observable,
      renamedFolder: observable,
      isErrorRenameFolder: observable,
      errorRenameFolderMessage: observable,

      // Actions
      setFolders: action,
      setLoading: action,
      setError: action,
      setCreatedFolder: action,
      setFolderAlreadyExists: action,
      setIsErrorCreateFolder: action,
      setFolderRenamed: action,
      setIsErrorRenameFolder: action,
      setErrorRenameFolderMessage: action,

      // Flows (async actions)
      getFoldersByDriveId: flow,
      getFoldersByParentId: flow,
      getMyFolders: flow,
      getSharedFolders: flow,
      getFolderById: flow,
      createFolder: flow,
      renameFolder: flow,
      deleteFolder: flow,
      duplicateFolder: flow
    });
  }

  // Actions
  setFolders = (folders) => {
    this.folders = folders;
  };

  setLoading = (status) => {
    this.isLoading = status;
  };

  setError = (error) => {
    this.error = error;
  };

  setCreatedFolder = (status) => {
    this.createdFolder = status;
    if (!status) {
      this.folderCreated = null;
    }
  };

  setFolderAlreadyExists = (status) => {
    this.folderAlreadyExists = status;
  };

  setIsErrorCreateFolder = (status) => {
    this.isErrorCreateFolder = status;
  };

  setFolderRenamed = (status) => {
    this.folderRenamed = status;
    if (!status) {
      this.renamedFolder = null;
    }
  };

  setIsErrorRenameFolder = (status) => {
    this.isErrorRenameFolder = status;
    if (!status) {
      this.errorRenameFolderMessage = '';
    }
  };

  setErrorRenameFolderMessage = (message) => {
    this.errorRenameFolderMessage = message;
  };

  // API Actions
  *getFoldersByDriveId(driveId) {
    try {
      this.setLoading(true);
      // Simulate service call
      const folders = FoldersData
      return folders;
    } catch (error) {
      this.setError(error);
      return [];
    } finally {
      this.setLoading(false);
    }
  }

  *getMyFolders() {
    try {
      this.setLoading(true);
      // Simulate service call
      const folders = FoldersData;
      this.setFolders(folders);
      return folders;
    } catch (error) {
      this.setError(error);
      return [];
    } finally {
      this.setLoading(false);
    }
  }


  *getFoldersByParentId(parentFolderId) {
    try {
      this.setLoading(true);
      // Simulate service call
      const folders = FoldersData
      this.setFolders(folders);
      return folders;
    } catch (error) {
      this.setError(error);
      return [];
    } finally {
      this.setLoading(false);
    }
  }

  *getSharedFolders() {
    try {
      this.setLoading(true);
      // Simulate service call
      const folders = FoldersData
      this.setFolders(folders);
      return folders;
    } catch (error) {
      this.setError(error);
      return [];
    } finally {
      this.setLoading(false);
    }
  }

  *getFolderById(id) {
    try {
      this.setLoading(true);
      // Simulate service call
      const folder = FoldersData.find(folder => folder.id === id);
      return folder || null;
    } catch (error) {
      this.setError(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  *createFolder(folderData) {
    try {
      this.isCreatingFolder = true;
      this.folderAlreadyExists = false;
      this.isErrorCreateFolder = false;

      const response = yield createFolderService(folderData);

      // Check statusCode in response.data (API wraps errors in HTTP 200)
      const { statusCode, msgCode, body } = response.data;

      // 2xx - Success (200 OK, 201 Created)
      if (statusCode >= 200 && statusCode < 300) {
        this.createdFolder = true;
        this.folderCreated = body;
        return this.folderCreated;
      }

      // 409 - Folder name conflict
      if (statusCode === 409 && msgCode && msgCode.some(msg => msg.code === "FOLDER_NAME_CONFLICT")) {
        this.folderAlreadyExists = true;
        return null;
      }

      // Other errors
      this.isErrorCreateFolder = true;
      return null;
      
    } catch (error) {
      console.error('Error in createFolder:', error);
      
      // Network error, endpoint does not exist, etc.
      console.error('Network or server error:', error.message);
      this.isErrorCreateFolder = true;
      
      return null;
    } finally {
      this.isCreatingFolder = false;
    }
  }
  *renameFolder(folderData) {
    try {
      this.isRenamingFolder = true;
      this.isErrorRenameFolder = false;
      this.errorRenameFolderMessage = '';

      const response = yield tenantRenameFolder(folderData);

      // Check statusCode in response.data (API wraps errors in HTTP 200)
      const { statusCode, msgCode, body } = response.data;
      
      // 2xx - Success (200 OK, 201 Created)
      if (statusCode >= 200 && statusCode < 300) {
        this.folderRenamed = true;
        this.renamedFolder = body;
        return body; // Return folder data
      }

      // Any other statusCode is an error
      this.isErrorRenameFolder = true;
      
      // Use error message from backend
      if (msgCode && msgCode.length > 0) {
        this.errorRenameFolderMessage = msgCode[0].description;
      } else {
        this.errorRenameFolderMessage = 'Error renaming folder.';
      }
      
      // Return empty object to indicate endpoint responded
      return {};
      
    } catch (error) {
      console.error('Error in renameFolder:', error);
      
      // Network error, endpoint does not exist, etc.
      this.isErrorRenameFolder = true;
      this.errorRenameFolderMessage = 'Network error. Please check your connection.';
      
      throw error; // Throw to be caught in component
    } finally {
      this.isRenamingFolder = false;
    }
  }

  *deleteFolder(id) {
    try {
      this.setLoading(true);
      // Simulate service call
      const index = FoldersData.findIndex(folder => folder.id === id);
      if (index !== -1) {
        FoldersData.splice(index, 1);
        return true;
      }
      return false;
    } catch (error) {
      this.setError(error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  *duplicateFolder(folderUuid) {
    try {
      this.setLoading(true);
      
      const response = yield duplicateFolderService(folderUuid);
      const duplicatedFolder = response.data.body;
      
      return duplicatedFolder;
    } catch (error) {
      console.error('Error in duplicateFolder:', error);
      
      // Fallback: Endpoint not available yet, create mock copy
      console.warn('Duplicate endpoint not available yet, creating mock copy');
      const mockFolder = {
        folder_uuid: `folder-${Date.now()}`,
        name: `Copy of Folder`,
        vault_uuid: 'mock-vault-uuid',
        parent_folder_uuid: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      return mockFolder;
    } finally {
      this.setLoading(false);
    }
  }
}

export default FoldersStore;